import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, CreditCard, CheckCircle, DollarSign, Users, Landmark } from 'lucide-react';
import api from '../utils/api';

const EMPTY_FORM = {
  name: '',
  type: 'business_plan',
  price: '',
  duration_months: 12,
  features: '',
  is_active: true,
};

const Subscriptions = () => {
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, avgPrice: 0 });
  const [loading, setLoading] = useState(true);
  
  const [showModal, setShowModal] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('plans'); // 'plans' | 'user-subs' | 'biz-subs'

  // User Subscriptions States
  const [userSubs, setUserSubs] = useState([]);
  const [userActiveCount, setUserActiveCount] = useState(0);
  const [userLoading, setUserLoading] = useState(false);

  // Business Subscriptions States
  const [businesses, setBusinesses] = useState([]);
  const [bizLoading, setBizLoading] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await api.get('/admin/subscription-plans');
      setPlans(res.data.data.plans);
      setStats(res.data.data.stats);
    } catch (err) {
      console.error('Fetch plans error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserSubscriptions = useCallback(async () => {
    setUserLoading(true);
    try {
      const res = await api.get('/admin/user-subscriptions');
      setUserSubs(res.data.data.subscriptions);
      setUserActiveCount(res.data.data.activeCount);
    } catch (err) {
      console.error('Fetch user subscriptions failed:', err);
    } finally {
      setUserLoading(false);
    }
  }, []);

  const fetchBusinesses = useCallback(async () => {
    setBizLoading(true);
    try {
      const res = await api.get('/admin/businesses');
      setBusinesses(res.data.data.businesses);
    } catch (err) {
      console.error('Fetch businesses failed:', err);
    } finally {
      setBizLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    if (activeTab === 'user-subs') {
      fetchUserSubscriptions();
    } else if (activeTab === 'biz-subs') {
      fetchBusinesses();
    }
  }, [activeTab, fetchUserSubscriptions, fetchBusinesses]);

  const openCreate = () => {
    setEditPlan(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (plan) => {
    setEditPlan(plan);
    setForm({
      name: plan.name,
      type: plan.type,
      price: plan.price,
      duration_months: plan.duration_months,
      features: Array.isArray(plan.features) ? plan.features.join(', ') : (plan.features || ''),
      is_active: plan.is_active,
    });
    setFormError('');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    try {
      await api.delete(`/admin/subscription-plans/${id}`);
      setPlans((prev) => prev.filter((p) => p.id !== id));
      fetchPlans();
    } catch (err) {
      alert('Failed to delete plan');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    const featuresArr = form.features
      ? form.features.split(',').map((f) => f.trim()).filter(Boolean)
      : [];
    const payload = {
      name: form.name,
      type: form.type,
      price: parseFloat(form.price),
      duration_months: parseInt(form.duration_months),
      features: featuresArr,
      is_active: form.is_active,
    };
    try {
      if (editPlan) {
        await api.put(`/admin/subscription-plans/${editPlan.id}`, payload);
      } else {
        await api.post('/admin/subscription-plans', payload);
      }
      setShowModal(false);
      fetchPlans();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save plan');
    } finally {
      setSubmitting(false);
    }
  };

  const planTypeLabel = (type) =>
    type === 'user_plan' ? 'User Plan' : 'Business Plan';

  const planTypeBadgeClass = (type) =>
    type === 'user_plan' ? 'type-badge user' : 'type-badge business';

  // Format Dates nicely to "8 May 2026"
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  // Filter only active premium businesses (excluding No Plan or inactive)
  const subscribedBusinesses = businesses.filter(
    (b) => b.plan_name && b.plan_name !== 'No Plan' && b.plan_status === 'active'
  );

  return (
    <div className="page-wrap">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Subscription Management</h1>
          <p className="page-subtitle">Manage subscription plans and active subscriptions</p>
        </div>
        {activeTab === 'plans' && (
          <button className="btn-primary" onClick={openCreate} id="create-plan-btn">
            <Plus size={16} /> Create Plan
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs-row">
        {['plans', 'user-subs', 'biz-subs'].map((tab) => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'tab-active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'plans' ? 'Subscription Plans' : tab === 'user-subs' ? 'User Subscriptions' : 'Business Subscriptions'}
          </button>
        ))}
      </div>

      {/* ── Tab 1: Subscription Plans ── */}
      {activeTab === 'plans' && (
        <>
          {/* Stats */}
          <div className="stats-grid three-col">
            <div className="stat-card">
              <CreditCard size={22} className="stat-icon blue" />
              <div>
                <p className="stat-label">Total Plans</p>
                <p className="stat-value">{stats.total}</p>
              </div>
            </div>
            <div className="stat-card">
              <CheckCircle size={22} className="stat-icon green" />
              <div>
                <p className="stat-label">Active Plans</p>
                <p className="stat-value">{stats.active}</p>
              </div>
            </div>
            <div className="stat-card">
              <DollarSign size={22} className="stat-icon blue" />
              <div>
                <p className="stat-label">Avg. Price</p>
                <p className="stat-value">₹{Number(stats.avgPrice).toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          {/* Plans Grid */}
          {loading ? (
            <div className="page-loading">Loading...</div>
          ) : (
            <div className="plans-grid">
              {plans.map((plan) => (
                <div key={plan.id} className="plan-card">
                  <div className="plan-card-header">
                    <div>
                      <h3 className="plan-name">{plan.name}</h3>
                      <span className={planTypeBadgeClass(plan.type)}>
                        {planTypeLabel(plan.type)}
                      </span>
                    </div>
                    <div className="plan-actions">
                      <button className="icon-btn" onClick={() => openEdit(plan)} title="Edit plan">
                        <Edit2 size={15} />
                      </button>
                      <button className="icon-btn danger" onClick={() => handleDelete(plan.id)} title="Delete plan">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  <div className="plan-price-row">
                    <span className="plan-price">₹{Number(plan.price).toLocaleString('en-IN')}</span>
                    <span className="plan-duration">per {plan.duration_months} months</span>
                  </div>

                  {/* Features */}
                  {plan.features && (
                    <ul className="plan-features">
                      {(Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features || '[]')).map((f, i) => (
                        <li key={i} className="plan-feature-item">✓ {f}</li>
                      ))}
                    </ul>
                  )}

                  <span className={`badge ${plan.is_active ? 'badge-verified' : 'badge-inactive'}`}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Tab 2: User Subscriptions ── */}
      {activeTab === 'user-subs' && (
        <div className="user-subs-section" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Stat Card */}
          <div className="stats-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="stat-card">
              <Users size={22} className="stat-icon blue" />
              <div>
                <p className="stat-label">Active User Subscriptions</p>
                <p className="stat-value">{userActiveCount}</p>
              </div>
            </div>
          </div>

          {/* User Table */}
          <div className="card table-card">
            {userLoading ? (
              <div className="page-loading" style={{ minHeight: '100px' }}>Loading subscriptions...</div>
            ) : (
              <div className="table-responsive">
                <table className="data-table" style={{ minWidth: '700px' }}>
                  <thead>
                    <tr>
                      <th>USER</th>
                      <th>PLAN</th>
                      <th>START DATE</th>
                      <th>END DATE</th>
                      <th>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userSubs.map((sub) => (
                      <tr key={sub.id}>
                        <td>
                          <div className="biz-cell">
                            <p className="biz-name" style={{ fontWeight: '700', color: '#0f172a' }}>{sub.user_name}</p>
                            <p className="biz-id" style={{ color: '#64748b' }}>{sub.user_email}</p>
                          </div>
                        </td>
                        <td>
                          <span style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                            {sub.plan_name}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>
                            {formatDate(sub.start_date)}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>
                            {formatDate(sub.end_date)}
                          </span>
                        </td>
                        <td>
                          <span 
                            className={`badge ${sub.status === 'Active' ? 'badge-verified' : 'badge-inactive'}`}
                            style={{ 
                              background: sub.status === 'Active' ? '#dcfce7' : '#fee2e2',
                              color: sub.status === 'Active' ? '#15803d' : '#b91c1c'
                            }}
                          >
                            {sub.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab 3: Business Subscriptions ── */}
      {activeTab === 'biz-subs' && (
        <div className="biz-subs-section" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Stat Card */}
          <div className="stats-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="stat-card">
              <Landmark size={22} className="stat-icon blue" />
              <div>
                <p className="stat-label">Active Business Subscriptions</p>
                <p className="stat-value">{subscribedBusinesses.length}</p>
              </div>
            </div>
          </div>

          {/* Business Table */}
          <div className="card table-card">
            {bizLoading ? (
              <div className="page-loading" style={{ minHeight: '100px' }}>Loading premium owners...</div>
            ) : (
              <div className="table-responsive">
                <table className="data-table" style={{ minWidth: '750px' }}>
                  <thead>
                    <tr>
                      <th>BUSINESS</th>
                      <th>PLAN</th>
                      <th>START DATE</th>
                      <th>END DATE</th>
                      <th>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribedBusinesses.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <div className="biz-cell">
                            <p className="biz-name" style={{ fontWeight: '700', color: '#0f172a' }}>{b.business_name}</p>
                            <p className="biz-id" style={{ color: '#64748b' }}>{b.category}</p>
                          </div>
                        </td>
                        <td>
                          <span style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                            {b.plan_name}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>
                            {formatDate(b.plan_start_date)}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>
                            {formatDate(b.plan_end_date)}
                          </span>
                        </td>
                        <td>
                          <span className="badge badge-verified">
                            Active
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editPlan ? 'Edit Plan' : 'Create New Plan'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              {formError && <div className="error-banner">{formError}</div>}
              <div className="form-group">
                <label className="form-label">Plan Name *</label>
                <input className="form-input" required value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Plan Type</label>
                <select className="form-input" value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                  <option value="business_plan">Business Plan</option>
                  <option value="user_plan">User Plan</option>
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Price (₹) *</label>
                  <input className="form-input" type="number" required value={form.price}
                    onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Duration (months)</label>
                  <input className="form-input" type="number" value={form.duration_months}
                    onChange={(e) => setForm((f) => ({ ...f, duration_months: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Features (comma-separated)</label>
                <textarea className="form-input" rows={3} value={form.features}
                  onChange={(e) => setForm((f) => ({ ...f, features: e.target.value }))}
                  placeholder="Feature 1, Feature 2, Feature 3" />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.is_active}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
                  Active
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editPlan ? 'Save Changes' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
